// Sous-plan 10, lot E2 : dans l'iframe de l'app (page Assistant), la colonne de Dust porte la
// navigation de l'app. L'app pose son modele sur window.__PIROUETTE_NAV__ (meme origine) avant de
// monter l'iframe et le renvoie par message a chaque changement ; le chat ne fait que le rendre et
// renvoie au parent des intentions (naviguer, reglages, compte) que l'app execute elle-meme.
import type { FenetreMinimale } from "@app/lib/compta/mode_integre";
import { estIntegre } from "@app/lib/compta/mode_integre";
import { useEffect, useState } from "react";

export type IconeNav =
  | "donnees"
  | "outils"
  | "fonctionnalites"
  | "administration"
  | "boite"
  | "taches";

export type EntreeNav = {
  id: string;
  libelle: string;
  icone?: IconeNav;
  route?: string;
  reglages?: string;
  /** Maquette de la coquille : l'adresse complete de la page dans l'app (meme origine). */
  adresse?: string;
  badge?: string;
  actif?: boolean;
  nonLu?: boolean;
  enfants?: EntreeNav[];
};

export type CompteNav = {
  nom: string;
  organisation: string;
  initiales: string;
  email?: string;
  theme: "light" | "dark" | "system";
};

export type NavigationApp = {
  version: 1;
  logo: string;
  entrees: EntreeNav[];
  compte: CompteNav;
  /**
   * Maquette de la coquille (01/10) : le chat porte la coquille (rectangle arrondi compris) et ouvre
   * les pages de l'app dans son rectangle (CadreDeLApp) au lieu de les renvoyer au parent.
   */
  coquille?: boolean;
  /** Coquille : la page de l'app a ouvrir au chargement (adresse /assistant?page=...). */
  pageInitiale?: string;
  /** Coquille : la page du chat a ouvrir au chargement, relative a l'espace (adresse /assistant?chat=...). */
  routeDuChat?: string;
  /**
   * Sous-plan 11 (une instance, plusieurs apps) : la section Administration d'Activités reprend
   * tous les enfants de l'entrée `administration` (Boosts). Absent : seulement les configurations
   * des webinaires et des quiz (Pirouette).
   */
  administrationComplete?: boolean;
  /** Sous-plan 11 : titre de la section métier d'Activités (Boosts : « Activités »). */
  titreActivites?: string;
  /**
   * Boite de reception (02/10) : l'entree « Boîte de réception » ouvre une petite fenetre que le chat
   * remplit lui-meme depuis l'API de l'app (`api`, meme origine) ; `nonLus` porte le badge. Absent
   * (ancienne app) : les notifications restent des entrees de la colonne (enfants de « boite »).
   */
  boite?: { api: string; nonLus: number };
};

export type MessageAuParent =
  | { type: "naviguer"; route: string }
  | { type: "reglages"; onglet: string }
  | { type: "emplacement"; route: string }
  | { type: "compte"; action: "reglages" | "deconnexion" }
  | { type: "compte"; action: "theme"; valeur: "light" | "dark" | "system" }
  // Languette de la messagerie de l'app (04/10) : largeur du panneau lateral ouvert (0 = ferme).
  | { type: "panneau"; largeur: number };

export interface FenetreNavigation extends FenetreMinimale {
  parent?: unknown;
  location?: { origin: string };
}

const SOURCE_APP = "pirouette-app";
const SOURCE_CHAT = "pirouette-chat";
const TYPE_NAVIGATION = "pirouette:nav";

function modeleValide(v: unknown): v is NavigationApp {
  if (typeof v !== "object" || v === null) {
    return false;
  }
  const m = v as { version?: unknown; entrees?: unknown; compte?: unknown };
  return (
    m.version === 1 &&
    Array.isArray(m.entrees) &&
    typeof m.compte === "object" &&
    m.compte !== null
  );
}

export function lireNavigationDuParent(
  fenetre: FenetreNavigation | undefined
): NavigationApp | null {
  if (!fenetre || !estIntegre(fenetre)) {
    return null;
  }
  try {
    const parent = fenetre.parent as
      | { __PIROUETTE_NAV__?: unknown }
      | undefined;
    const v = parent?.__PIROUETTE_NAV__;
    return modeleValide(v) ? v : null;
  } catch {
    // Parent d'origine etrangere : rien a lire.
    return null;
  }
}

export function navigationDuMessage(
  event: MessageEvent,
  fenetre: FenetreNavigation | undefined
): NavigationApp | null {
  if (!fenetre || !estIntegre(fenetre)) {
    return null;
  }
  try {
    if (
      event.origin !== fenetre.location?.origin ||
      event.source !== fenetre.parent
    ) {
      return null;
    }
  } catch {
    return null;
  }
  const d = event.data as {
    source?: unknown;
    type?: unknown;
    nav?: unknown;
  } | null;
  if (typeof d !== "object" || d === null) {
    return null;
  }
  if (d.source !== SOURCE_APP || d.type !== TYPE_NAVIGATION) {
    return null;
  }
  return modeleValide(d.nav) ? d.nav : null;
}

export function envoyerAuParent(
  fenetre: FenetreNavigation | undefined,
  message: MessageAuParent
): void {
  if (!fenetre || !estIntegre(fenetre)) {
    return;
  }
  try {
    const parent = fenetre.parent as
      | { postMessage?: (m: unknown, origine: string) => void }
      | undefined;
    parent?.postMessage?.(
      { source: SOURCE_CHAT, ...message },
      fenetre.location?.origin ?? ""
    );
  } catch {
    // Parent injoignable : rien a envoyer.
  }
}

function fenetreCourante(): FenetreNavigation | undefined {
  return typeof window === "undefined"
    ? undefined
    : (window as unknown as FenetreNavigation);
}

export function useNavigationDeLApp(): NavigationApp | null {
  const [nav, setNav] = useState<NavigationApp | null>(() =>
    lireNavigationDuParent(fenetreCourante())
  );
  useEffect(() => {
    const f = fenetreCourante();
    if (!f || !estIntegre(f)) {
      return;
    }
    // L'app a pu poser le modele apres le premier rendu : relire, puis suivre ses messages.
    const relu = lireNavigationDuParent(f);
    if (relu) {
      setNav(relu);
    }
    const surMessage = (event: MessageEvent) => {
      const n = navigationDuMessage(event, f);
      if (n) {
        setNav(n);
      }
    };
    window.addEventListener("message", surMessage);
    return () => window.removeEventListener("message", surMessage);
  }, []);
  return nav;
}

export function envoyer(message: MessageAuParent): void {
  envoyerAuParent(fenetreCourante(), message);
}
